import React, { useEffect, useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  ThemeIcon,
  Loader,
  Badge,
  Box
} from '@mantine/core';
import {
  IconShieldCheck,
  IconCircleCheck,
  IconCircleDot
} from '@tabler/icons-react';

/**
 * Live-progress modal shown while the LLM-as-judge is evaluating a generated
 * artifact (SIT plans, AgentStudio agent JSON, AgentStudio flow JSON).
 *
 * The bullet items are the rubric the judge is ACTUALLY checking on the
 * engineer's behalf — read straight from the judge's system prompt — so the
 * snippets are honest even though the modal doesn't have a live trace of which
 * check is running right now. Items cycle through with a checkmark animation
 * to make the wait feel purposeful.
 *
 * Parent controls `opened` — flip it to false the instant the judge resolves
 * and the modal closes itself. There's no separate "complete" state inside the
 * modal; the parent already has the badge + findings panel to render the
 * result.
 *
 * Props:
 *   opened    — Mantine Modal open state. Caller closes on judge resolve.
 *   onClose   — Mantine Modal close handler. The modal doesn't auto-close from
 *               within (we don't know when the judge finishes); the caller
 *               passes `opened={evaluating}` so flipping evaluating→false
 *               closes the modal.
 *   kind      — "sit-plan" | "agentstudio-agent" | "agentstudio-flow".
 *               Drives which rubric bullets render.
 *   color     — Mantine color name (violet for SIT, cyan for AgentStudio).
 *   modelHint — optional Bedrock model badge text (e.g. "Sonnet 4.6").
 */

const RUBRICS = {
  'sit-plan': {
    title: 'Quality-judging your test plans',
    subtitle:
      'Sonnet 4.6 is reading the source you imported and grading each generated plan against this rubric. ~15–25s typical.',
    items: [
      {
        label: 'Faithfulness',
        detail:
          'Every expectPrompt intent has to be grounded in the source — no inventing facts the importer can\'t back up.'
      },
      {
        label: 'Completeness',
        detail:
          'The obvious caller paths from your source should each show up at least once across the plan set.'
      },
      {
        label: 'Step correctness',
        detail:
          'Matcher type fits the step — semantic for AI / conversational, substring only for verbatim quoted phrases.'
      },
      {
        label: 'Terminality',
        detail:
          'Every plan must end in hangup or expectTransfer; expectPrompt as the last step is an incomplete plan.'
      },
      {
        label: 'No brand hallucination',
        detail:
          'Plans stay portable — no tenant / company / product name in the intent unless the source mentions it.'
      }
    ]
  },
  'agentstudio-agent': {
    title: 'Quality-judging your generated agent',
    subtitle:
      'Sonnet 4.6 is comparing your form input + the standards file against the generated agent JSON. ~15–25s typical.',
    items: [
      {
        label: 'Standards coverage',
        detail:
          'Each rule from wxcc.json behaviorRules (escalation, PCI, confirmation gates, persona consistency, ...) is reflected in llm_agent_description.'
      },
      {
        label: 'Persona consistency',
        detail:
          'If you asked for "clinical", the agent stays clinical throughout — greeting, error recovery, closing — not drifting into "friendly" mid-instructions.'
      },
      {
        label: 'Spec coverage',
        detail:
          'Every slot / action / transfer target you supplied has a corresponding tool or description block, with vivid enough wording for the LLM to pick at runtime.'
      },
      {
        label: 'Hard constraints',
        detail:
          'llm_agent_description ≤ 5120 chars, tools[] ≤ 10 entries, name ≤ 256 chars, per-tool description ≤ 1024 chars.'
      },
      {
        label: 'Webhook placeholder discipline',
        detail:
          'Custom tools point at the standard PLACEHOLDER hook (engineer wires up the real URL in WxCC after import) — no example.com, no <TO_BE_FILLED>.'
      }
    ]
  },
  'agentstudio-flow': {
    title: 'Quality-judging your synthesized flow',
    subtitle:
      'Sonnet 4.6 is comparing the bot inventory + tenant template against the generated WxCC flow JSON. ~15–25s typical.',
    items: [
      {
        label: 'Edge correctness',
        detail:
          'Every exitsToBots edge in your bot inventory is routed via a Case activity with a matching <verb_object>_exit branch.'
      },
      {
        label: 'Port template adherence',
        detail:
          'Each activity\'s ports match the tenant template shape — VAV2 has in/error/ENDED/ESCALATE, Case has 0..N/in/error/default, etc.'
      },
      {
        label: 'Custom event naming',
        detail:
          '<verb_object>_exit for outbound events, <verb_object>_confirm_entry for resume templates. No ad-hoc "do_thing" names.'
      },
      {
        label: 'No dangling links',
        detail: 'Every link\'s source + target references a valid activityId in the flow — no orphans.'
      },
      {
        label: 'HTTPRequest discipline',
        detail:
          'HTTPRequest activities reference a tenant connectorId (you wire it up post-import), not a raw URL string.'
      }
    ]
  },
  'wxcc-function': {
    title: 'Quality-judging your WxCC function code',
    subtitle:
      'Sonnet 4.6 is grading the generated JavaScript against the engineer\'s prompt + Cisco\'s WxCC Flow Designer Function runtime rules. ~15–25s typical.',
    items: [
      {
        label: 'Signature + return',
        detail:
          'Code uses the exact `export const handle = async (request, response) => {...}` signature and every path ends in `return response`.'
      },
      {
        label: 'Try/catch + input guards',
        detail:
          'Body wrapped in try/catch with 500 on unexpected errors; every request.inputs.X has an early 400 guard before first use.'
      },
      {
        label: 'Lookup not-found handling',
        detail:
          'Every .find() / index lookup has a 404 path; no .id access on possibly-undefined matches.'
      },
      {
        label: 'No custom modules',
        detail:
          'Cisco\'s runtime forbids them — no require("axios"), no import lodash, native Node 22 stdlib + global fetch only.'
      },
      {
        label: 'Pattern matching + determinism',
        detail:
          'Case-insensitive compares, anchored regex for suffix stripping (e.g. /_EP$/i), no Date.now() or Math.random().'
      },
      {
        label: 'Dataset completeness',
        detail:
          'When you selected entity data, every entity is in the code (or the template explicitly marks where to paste the rest).'
      },
      {
        label: 'Cisco hard limits',
        detail:
          '≤5000 lines of code, ≤10 distinct input variables, ≤128 MB memory, 5-second execution timeout.'
      }
    ]
  }
};

const CYCLE_MS = 1400;

function JudgeProgressModal({ opened, onClose, kind, color = 'violet', modelHint = 'Sonnet 4.6' }) {
  const rubric = RUBRICS[kind] || RUBRICS['sit-plan'];
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!opened) {
      setActiveIdx(0);
      return undefined;
    }
    const id = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % rubric.items.length);
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [opened, rubric.items.length]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      centered
      size="lg"
      overlayProps={{ backgroundOpacity: 0.55, blur: 2 }}
      // Engineers shouldn't accidentally dismiss the modal mid-judge.
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack gap="md">
        <Group gap="sm" align="center">
          <ThemeIcon color={color} variant="light" size="lg" radius="xl">
            <IconShieldCheck size={20} />
          </ThemeIcon>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap">
              <Text fw={600} size="md">{rubric.title}</Text>
              <Loader size="xs" color={color} />
            </Group>
            <Text size="xs" c="dimmed">{rubric.subtitle}</Text>
          </Stack>
          <Badge variant="outline" color="gray">{modelHint}</Badge>
        </Group>

        <Box>
          <Text size="xs" c="dimmed" mb={6}>What the judge is evaluating</Text>
          <Stack gap={6}>
            {rubric.items.map((item, i) => {
              const isActive = i === activeIdx;
              const isDone = i < activeIdx;
              return (
                <Group key={item.label} gap="xs" wrap="nowrap" align="flex-start">
                  <ThemeIcon
                    variant={isActive ? 'filled' : isDone ? 'light' : 'outline'}
                    color={isActive ? color : isDone ? color : 'gray'}
                    size="sm"
                    radius="xl"
                    mt={2}
                  >
                    {isActive ? <IconCircleDot size={12} /> : isDone ? <IconCircleCheck size={12} /> : <IconCircleDot size={12} />}
                  </ThemeIcon>
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={isActive ? 600 : 500} c={isActive ? undefined : isDone ? undefined : 'dimmed'}>
                      {item.label}
                    </Text>
                    <Text
                      size="xs"
                      c="dimmed"
                      style={{
                        // Active item is fully visible; cycled-past items
                        // fade slightly so the eye lands on what's "current".
                        opacity: isActive ? 1 : 0.75
                      }}
                    >
                      {item.detail}
                    </Text>
                  </Stack>
                </Group>
              );
            })}
          </Stack>
        </Box>

        <Text size="xs" c="dimmed" ta="center">
          This pass is advisory — it never changes the artifact you already generated. You'll see a
          score badge + findings under the result panel as soon as it finishes.
        </Text>
      </Stack>
    </Modal>
  );
}

export default JudgeProgressModal;
